import React, { useContext, useEffect, useRef, useState, useCallback } from 'react'
import { useIntl } from 'react-intl'

// components
import SalonsCategoryPageHead from './components/SalonsCategoryPageHead/SalonsCategoryPageHead'
import SalonsSeoSchema from '../../components/SalonsSeoSchema/SalonsSeoSchema'
import SalonsView from '../../components/SalonsView/SalonsView'
import Filters from '../../components/Filters/Filters'
import SalonsAndCitiesSearchWrap from '../../components/SalonsAndCitiesSearchWrap/SalonsAndCitiesSearchWrap'
import SalonsPageBanner from './components/SalonsPageBanner/SalonsPageBanner'
import SubcategoriesNavigation from './components/SubcategoriesNavigation/SubcategoriesNavigation'

// types
import {
	Currency,
	GetSalonsDataParams,
	CategoryResponse,
	ConfigResponse,
	GetSalonsFilterCountResponse,
	SalonsResponse,
	LoadSalonsFromFiltersParams,
	SalonsCategoryPageQueryType,
	CmsSalonsCategoryPageData,
	Cosmetic,
	Language,
	PageProps,
	SalonsListPageQueryType
} from '../../types/types'

// styles
import * as SC from './SalonsCategoryPageStyles'

// utils
import createPageContainer from '../../utils/createPageContainer'
import { AppContext } from '../../utils/appProvider'
import { MyLocationContext } from '../../utils/myLocationProvider'
import { areReservationsAllowed, getCategoryIDs, isNil, PAGE_LINKS } from '../../utils/helper'
import { pushToDataLayer } from '../../utils/dataLayer'
import { getPageViewEvent, getViewItemListEvent } from '../../utils/dataLayerEvents'
import { shopsConfig } from '../../appDefaults'
import { GET_MY_LOCATION_OPTION } from '../../components/SalonsAndCitiesSearch/SalonsAndCitiesSearch'
import { ABORT_KEYS, SESSION_STORAGE_KEYS } from '../../utils/enums'
import { getCategorySeoData } from '../../utils/categoriesSeoData'

// hooks
import useMessages from '../../hooks/useMessages'

// types
type RouteProps = {
	categorySlug: string
}

type SalonsCategoryPageData = {
	salonsData: SalonsResponse
	categoryData: CategoryResponse
	configData: ConfigResponse
	currency: Currency
	filterCountsData: GetSalonsFilterCountResponse
	allowedReservations: boolean
	cmsData: CmsSalonsCategoryPageData
}

type SalonsCategoryPageProps = PageProps<SalonsCategoryPageData, SalonsCategoryPageQueryType, RouteProps>

// page
const SalonsCategoryPage = (props: SalonsCategoryPageProps) => {
	const { setPageData, query, setQuery, categorySlug, salonsData, categoryData, configData, cmsData, currency, filterCountsData, allowedReservations } = props

	const { salons, pagination } = salonsData

	const { messages } = useMessages()
	const { locale } = useIntl()
	const { apiBrowser } = useContext(AppContext)
	const { myLocation, isLoadingMyLocation } = useContext(MyLocationContext)

	const [cosmetics, setCosmetics] = useState<Cosmetic[]>([])
	const [languages, setLanguages] = useState<Language[]>([])

	const [initialLocationBasedLoading, setInitialLocationBasedLoading] = useState(isLoadingMyLocation)
	const [areSalonsLoading, setAreSalonsLoading] = useState<boolean>(false)
	const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)
	const [initialClientDataLoading, setInitialClientDataLoading] = useState(true)

	const isMountFetchRef = useRef(true)
	const isMyPositionKnownOnMount = useRef(!!myLocation)
	const mergeWithPreviousSalons = useRef(false)

	const defaultCity = configData.rolloutCountries.find((country) => country.languageCode === locale)?.capitalCity

	const MY_LOCATION_OPTION = GET_MY_LOCATION_OPTION()

	const refetchSalonsAndFilterCountsFromLoadMoreBtn = () => {
		setIsButtonLoading(true)

		// set mergeWithPreviousSalons flag to true so the currently showed salons can be merged with newly loaded salons
		mergeWithPreviousSalons.current = true

		setQuery({
			...query,
			page: query.page + 1
		})
	}

	const getLocationParams = (city: LoadSalonsFromFiltersParams['city']) => {
		const isUsingMyPosition = city?.placeID === MY_LOCATION_OPTION.value

		let lat: number | undefined
		let lon: number | undefined
		let latMy: number | undefined
		let lonMy: number | undefined
		let googlePlaceID: string | undefined

		if (myLocation || isUsingMyPosition) {
			latMy = myLocation?.latMy || city?.latitude
			lonMy = myLocation?.lonMy || city?.longitude
		}

		if (!isUsingMyPosition && city !== null) {
			lat = city?.latitude ?? query.lat
			lon = city?.longitude ?? query.lon
			googlePlaceID = city?.placeID ?? query.googlePlaceID
		}

		return { lat, lon, latMy, lonMy, googlePlaceID, isUsingMyPosition }
	}

	const updateQueryFromFilters = async (params: LoadSalonsFromFiltersParams) => {
		const {
			openingHoursStatus: openingHoursStatusParam,
			city,
			categoryIDs: categoryIDsParam,
			orderBy: orderByParam,
			exactRating: exactRatingParam,
			languageIDs: languageIDsParam,
			cosmeticIDs: cosmeticIDsParam,
			serviceTotalPriceFrom: serviceTotalPriceFromParam,
			serviceTotalPriceTo: serviceTotalPriceToParam,
			hasAvailableReservationSystem: hasAvailableReservationSystemParam,
			avResTimeSlotDayPart: avResTimeSlotDayPartParam,
			avResTimeSlotDate: avResTimeSlotDateParam,
			avResTimeSlotDateFrom: avResTimeSlotDateFromParam,
			avResTimeSlotDateTo: avResTimeSlotDateToParam
		} = params

		const openingHoursStatus = openingHoursStatusParam || query.openingHoursStatus
		const categoryIDs = categoryIDsParam || query.categoryIDs
		const orderBy = orderByParam || query.orderBy
		const exactRating = exactRatingParam || query.exactRating
		const languageIDs = languageIDsParam || query.languageIDs
		const cosmeticIDs = cosmeticIDsParam || query.cosmeticIDs
		const avResTimeSlotDayPart = avResTimeSlotDayPartParam || query.avResTimeSlotDayPart

		// using null to purposely reset value in following params
		const serviceTotalPriceFrom = serviceTotalPriceFromParam === null ? undefined : (serviceTotalPriceFromParam ?? query.serviceTotalPriceFrom)
		const serviceTotalPriceTo = serviceTotalPriceToParam === null ? undefined : (serviceTotalPriceToParam ?? query.serviceTotalPriceTo)
		const avResTimeSlotDate = avResTimeSlotDateParam === null ? undefined : avResTimeSlotDateParam || query.avResTimeSlotDate
		const avResTimeSlotDateFrom = avResTimeSlotDateFromParam === null ? undefined : avResTimeSlotDateFromParam || query.avResTimeSlotDateFrom
		const avResTimeSlotDateTo = avResTimeSlotDateToParam === null ? undefined : avResTimeSlotDateToParam || query.avResTimeSlotDateTo
		const hasAvailableReservationSystem =
			hasAvailableReservationSystemParam !== undefined ? hasAvailableReservationSystemParam : query.hasAvailableReservationSystem

		const { lat, lon, googlePlaceID } = getLocationParams(city)

		// update query
		setQuery({
			openingHoursStatus,
			page: 1,
			lat,
			lon,
			categoryIDs,
			googlePlaceID,
			orderBy,
			exactRating,
			languageIDs,
			cosmeticIDs,
			serviceTotalPriceFrom,
			serviceTotalPriceTo,
			hasAvailableReservationSystem,
			avResTimeSlotDayPart,
			avResTimeSlotDate,
			avResTimeSlotDateFrom,
			avResTimeSlotDateTo
		})
	}

	const fetchSalonsAndFilterCounts = useCallback(
		async (mergeWithPrevious = false) => {
			const categoryIDs = getCategoryIDs(query.categoryIDs, categoryData.category.id)

			let commonParams: GetSalonsDataParams = {
				openingHoursStatus: query.openingHoursStatus,
				latMy: myLocation?.latMy,
				lonMy: myLocation?.lonMy,
				lat: query.lat,
				lon: query.lon,
				categoryIDs,
				exactRating: query.exactRating,
				hasAvailableReservationSystem: query.hasAvailableReservationSystem,
				languageIDs: query.languageIDs,
				cosmeticIDs: query.cosmeticIDs,
				orderBy: query.orderBy,
				avResTimeSlotDayPart: query.avResTimeSlotDayPart,
				avResTimeSlotDate: query.avResTimeSlotDate,
				avResTimeSlotDateFrom: query.avResTimeSlotDateFrom,
				avResTimeSlotDateTo: query.avResTimeSlotDateTo
			}

			if (!isNil(query.serviceTotalPriceFrom) || !isNil(query.serviceTotalPriceTo)) {
				commonParams = {
					...commonParams,
					serviceTotalPriceFrom: query.serviceTotalPriceFrom,
					serviceTotalPriceTo: query.serviceTotalPriceTo,
					serviceTotalPriceCurrencyCode: currency.code
				}
			}

			const { data, isCancelTokenError } = await setPageData(
				[
					{
						dataKey: 'salonsData',
						data: apiBrowser.b2c.getSalonsData(
							{ ...commonParams, page: query.page },
							{ allowAbort: true, abortSignalKey: ABORT_KEYS.SALONS_ABORT_KEY }
						)
					},
					{
						dataKey: 'filterCountsData',
						data: apiBrowser.b2c.getSalonsFilterCounts(
							{ ...commonParams, serviceTotalPriceCurrencyCode: currency.code },
							{ allowAbort: true, abortSignalKey: ABORT_KEYS.SALONS_FILTER_COUNT_ABORT_KEY }
						)
					}
				],
				{
					onSuccess: (prevData, newData) => {
						if (!mergeWithPrevious) {
							return newData
						}

						const mergedData = {
							...prevData,
							salonsData: {
								...prevData.salonsData,
								pagination: newData.salonsData.pagination,
								salons: [...prevData.salonsData.salons, ...newData.salonsData.salons]
							},
							filterCountsData: {
								...newData.filterCountsData
							}
						}

						return mergedData
					}
				}
			)

			if (data?.success) {
				const startIdx = (data.salonsData.pagination.page - 1) * data.salonsData.pagination.limit
				const latestAddedSalons = data.salonsData.salons.slice(startIdx, startIdx + data.salonsData.pagination.limit)

				// push to dataLayer
				const event = getViewItemListEvent({
					currentPage: query.page || 1,
					currentSortingOptionName: query.orderBy,
					salons: latestAddedSalons
				})
				pushToDataLayer(event)
			}

			return { isCancelTokenError }
		},
		[
			apiBrowser,
			query.page,
			query.openingHoursStatus,
			query.orderBy,
			query.exactRating,
			query.categoryIDs,
			query.lat,
			query.lon,
			query.languageIDs,
			query.cosmeticIDs,
			query.serviceTotalPriceFrom,
			query.serviceTotalPriceTo,
			query.hasAvailableReservationSystem,
			query.avResTimeSlotDayPart,
			query.avResTimeSlotDate,
			query.avResTimeSlotDateFrom,
			query.avResTimeSlotDateTo,
			currency.code,
			categoryData.category.id,
			myLocation?.latMy,
			myLocation?.lonMy,
			setPageData
		]
	)

	useEffect(() => {
		/**
		 * Fetch new data whenever some of the dependencies changes after the first data load
		 */
		;(async () => {
			if (!isMountFetchRef.current && !query.isMapView) {
				setAreSalonsLoading(true)
				const { isCancelTokenError } = await fetchSalonsAndFilterCounts(mergeWithPreviousSalons.current)
				if (!isCancelTokenError) {
					setAreSalonsLoading(false)
					setIsButtonLoading(false)
				}
				mergeWithPreviousSalons.current = false
			}
		})()
	}, [fetchSalonsAndFilterCounts, query.isMapView])

	useEffect(() => {
		/**
		 * Salons are already fetched on the server side, so client-side fetching is not always necessary on page load.
		 * However, since we are working with `myLocation`, which is not available on the server, we need to handle location-based fetching.
		 * If the user grants permission to access their location, we want to refetch the data using the user's location.
		 * During this process, we show an initial loading state until the location is resolved, and if needed, fetch new data.
		 * If the user's location is already known from query parameters at page initialization, there is no need to show the initial loading state.
		 * The loading state is displayed over the salons fetched from the server, ensuring that the data is available in the HTML for search engine bots, while preventing a content flash for users.
		 */
		;(async () => {
			if (isLoadingMyLocation || !isMountFetchRef.current || query.isMapView) {
				return
			}

			let isCancelTokenError = false

			if (myLocation && !isMyPositionKnownOnMount.current) {
				isCancelTokenError = (await fetchSalonsAndFilterCounts()).isCancelTokenError
			}

			if (!isCancelTokenError) {
				setInitialLocationBasedLoading(false)
			}
			isMountFetchRef.current = false
		})()
	}, [isLoadingMyLocation, myLocation, fetchSalonsAndFilterCounts, query.isMapView])

	useEffect(() => {
		;(async () => {
			try {
				const [cosmeticsData, languagesData] = await Promise.all([apiBrowser.b2c.getCosmeticsData({}), apiBrowser.b2c.getLanguagesData()])

				setLanguages(languagesData.languages)
				setCosmetics(cosmeticsData.cosmetics)
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error(e)
			} finally {
				setInitialClientDataLoading(false)
			}
		})()
	}, [apiBrowser])

	useEffect(() => {
		// GA page view event
		const pageViewEvent = getPageViewEvent('salons', 'salons')
		pushToDataLayer(pageViewEvent)
	}, [])

	useEffect(() => {
		// remove helper data for GA event getViewItemListEvent from session storage when page unmounts or is refreshed
		// check getViewItemListEvent function for more info
		const onPageLeave = () => sessionStorage.setItem(SESSION_STORAGE_KEYS.IS_SALONS_SEARCH, '')

		window.addEventListener('beforeunload', onPageLeave)

		return () => {
			window.removeEventListener('beforeunload', onPageLeave)
			onPageLeave()
		}
	}, [])

	const categorySeoData = getCategorySeoData(categoryData.category.id, categoryData.category.name ?? '')

	const getBreadcrumbItems = (hasSalonsBreadcrumb: boolean, queryParams?: SalonsListPageQueryType) => {
		const items = []

		if (hasSalonsBreadcrumb) {
			items.push({ link: PAGE_LINKS['/salons/homepage'](locale), name: messages.Salons })
		}

		items.push({ link: PAGE_LINKS['/salons'](locale, queryParams), name: messages['List of salons'] })

		if (categoryData.category.name) {
			items.push({ link: '', name: categoryData.category.name })
		}

		return items
	}

	const seoSchemaBreadcrumbItems = getBreadcrumbItems(true)

	const { categoryIDs: _, ...filteredQuery } = query
	const filterQueryParams: SalonsListPageQueryType = { ...myLocation, ...filteredQuery, page: 1 }
	const breadcrumbItems = getBreadcrumbItems(false, filterQueryParams)

	return (
		<>
			<SalonsSeoSchema breadcrumbItems={seoSchemaBreadcrumbItems} />
			<SalonsCategoryPageHead pagination={pagination} categoryLocalizations={categoryData.category.nameLocalizations} categorySeoData={categorySeoData} />
			<SC.Container>
				<SC.SalonsPageWrapper>
					<SC.Grid>
						<SC.SalonsPageBreadcrumbs breadcrumbItems={breadcrumbItems} hasSalonsBreadcrumb />

						<SalonsPageBanner cmsData={cmsData.bannersData} />

						<SC.MainTitle>{categoryData.category.name}</SC.MainTitle>

						<SalonsAndCitiesSearchWrap query={query} loadSalonsFromFilters={updateQueryFromFilters} currentCategoryID={categoryData.category.id} />

						<SubcategoriesNavigation
							categoryData={categoryData}
							categorySlug={categorySlug}
							query={query}
							updateQueryFromFilters={updateQueryFromFilters}
						/>

						<Filters
							query={query}
							filterCounts={filterCountsData}
							currencySymbol={currency?.symbol}
							currencyCode={currency?.code}
							cosmetics={cosmetics}
							languages={languages}
							loadSalonsFromFilters={updateQueryFromFilters}
							initialClientDataLoading={initialClientDataLoading}
							topLevelCategoryID={categoryData.category.id}
							maxAvResTimeSlotDateRange={configData?.maxAvResTimeSlotDateRange}
						/>

						<SalonsView
							salons={salons}
							pagination={pagination}
							categoryData={categoryData}
							query={query}
							setQuery={setQuery}
							loadSalonsFromLoadMoreButton={refetchSalonsAndFilterCountsFromLoadMoreBtn}
							loadSalonsFromFilters={updateQueryFromFilters}
							initialLoading={initialLocationBasedLoading}
							salonsLoading={areSalonsLoading}
							buttonLoading={isButtonLoading}
							currencyCode={currency.code}
							defaultCity={defaultCity}
							allowedReservations={allowedReservations}
							cmsData={{
								appPromoData: cmsData.appPromoData,
								smallBannerData: cmsData.salonListSmallBannerData
							}}
						/>
						{categorySeoData.categoryDescription && !query.isMapView && (
							<SC.CategoryDescriptionContainer>
								{categorySeoData.categoryDescriptionTitle && (
									<SC.CategoryDescriptionTitle>{categorySeoData.categoryDescriptionTitle}</SC.CategoryDescriptionTitle>
								)}
								<SC.CategoryDescription>{categorySeoData.categoryDescription}</SC.CategoryDescription>
							</SC.CategoryDescriptionContainer>
						)}
					</SC.Grid>
				</SC.SalonsPageWrapper>
			</SC.Container>
		</>
	)
}

// page container
const SalonsCategoryPageContainer = createPageContainer<SalonsCategoryPageData, SalonsCategoryPageQueryType, RouteProps>({
	identifier: 'SalonsCategoryPage',
	PageComponent: SalonsCategoryPage,
	initDefaultData: async ({ api, query, categorySlug, locale, shopId }) => {
		const [categoriesData, configData, cmsData] = await Promise.all([
			api.b2c.getCategoriesData(),
			api.b2c.getConfigData(),
			api.cms.getSalonsCategoryPageCmsData()
		])

		const localeCode = shopsConfig.find((config) => config.lang === locale)?.locale
		const currencyCode = configData.rolloutCountries.find((country) => country.languageCode === localeCode)?.currencyCode
		const currency = configData.systemCurrencies.find((c) => c.code === currencyCode)

		const foundCategory = categoriesData.categories.find((category) => category.nameSlug === categorySlug)

		if (!currency) {
			throw new Error('Currency not found')
		}

		if (!foundCategory) {
			throw new Error('Category not found')
		}

		const allowedReservations = areReservationsAllowed(configData.allowReservations, shopId)

		const categoryIDs = query.categoryIDs ? getCategoryIDs(query.categoryIDs, foundCategory.id) : undefined

		let getSalonsDataParams: GetSalonsDataParams = {
			openingHoursStatus: query.openingHoursStatus,
			latMy: query.latMy,
			lonMy: query.lonMy,
			lat: query.lat,
			lon: query.lon,
			categoryIDs,
			exactRating: query.exactRating,
			languageIDs: query.languageIDs,
			cosmeticIDs: query.cosmeticIDs,
			hasAvailableReservationSystem: query.hasAvailableReservationSystem,
			orderBy: query.orderBy,
			avResTimeSlotDayPart: query.avResTimeSlotDayPart,
			avResTimeSlotDate: query.avResTimeSlotDate,
			avResTimeSlotDateFrom: query.avResTimeSlotDateFrom,
			avResTimeSlotDateTo: query.avResTimeSlotDateTo
		}

		if (!isNil(query.serviceTotalPriceFrom) || !isNil(query.serviceTotalPriceTo)) {
			getSalonsDataParams = {
				...getSalonsDataParams,
				serviceTotalPriceFrom: query.serviceTotalPriceFrom,
				serviceTotalPriceTo: query.serviceTotalPriceTo,
				serviceTotalPriceCurrencyCode: currency.code
			}
		}

		const [categoryData, salonsData, filterCountsData] = await Promise.all([
			api.b2c.getCategoryData(foundCategory.id),
			api.b2c.getSalonsData({
				...getSalonsDataParams,
				page: query.page
			}),
			api.b2c.getSalonsFilterCounts({ ...getSalonsDataParams, serviceTotalPriceCurrencyCode: currency.code })
		])

		return { salonsData, categoryData, configData, currency, filterCountsData, cmsData, allowedReservations }
	}
})

export default SalonsCategoryPageContainer
